import { Dialog, Transition } from '@headlessui/react';
import { FilterIcon } from 'components/icons';
import SaveIcon from 'components/icons/SaveIcon';
import { ButtonCTASmall } from 'components/ui';
import { omit } from 'lodash';
import querystring from 'querystring';
import { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { useHistory } from 'react-router';
import Select, { SingleValue } from 'react-select';
import { useAppDispatch, useAppSelector } from 'store';
import {
  applySelected,
  cancelApplying,
  clearSelected,
  composableFiltersState,
  MainOperatorEnum,
  selectMainOperator,
} from 'store/composableFiltersSlice';
import { useParseURLQueryParams } from 'utils';
import { SegmentSelector } from '../SegmentSelector';
import { AdvancedFiltersGrid } from './AdvancedFiltersGrid';
import { EditSegment } from './EditSegment';
import { FavoriteSegment } from './FavoriteSegment';
import { RemoveSegment } from './RemoveSegment';
import { SaveSegment } from './SaveSegment';

interface FilterModalProps {
  modalOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const FilterModal = ({ modalOpen, setModalOpen }: FilterModalProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [advancedGrid] = useState(true);

  const dispatch = useAppDispatch();
  const { rawQueryParams } = useParseURLQueryParams();
  const history = useHistory();

  const handleCancel = () => {
    setModalOpen(false);
    dispatch(cancelApplying());
    if (rawQueryParams?.segment_name) {
      history.push({
        search: querystring.stringify(
          omit({ ...rawQueryParams }, ['segment_name'])
        ),
      });
    }
  };

  const handleApplyFilters = () => {
    setModalOpen(false);
    dispatch(applySelected());
  };

  const { selected } = useAppSelector(composableFiltersState);

  const isManagedSegment = Boolean(
    selected.segment?.json_filter?.filter?.filters?.find((filter) =>
      filter.name?.startsWith('managed_segment')
    )
  );

  return (
    <Transition show={modalOpen} as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        static
        open={modalOpen}
        onClose={handleCancel}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm " />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle "
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <main className="inline-block my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex flex-col justify-between h-full">
                <div className="">
                  <InnerSegmentSelectionBar />
                  <div className="ml-3">
                    <InnerFilterBar advancedGrid={advancedGrid} />
                  </div>
                  {isManagedSegment ? (
                    <ManagedSegmentsView />
                  ) : (
                    <AdvancedFiltersGrid />
                  )}
                </div>
                {/* Footer Starts */}
                <div className="py-8 mx-10">
                  <div className="flex items-center justify-between">
                    {/* IMPORTANT: Grid toggle disabled temporariily */}
                    {/* <GridViewToggle
                      advancedGrid={advancedGrid}
                      setAdvancedGrid={setAdvancedGrid}
                    /> */}
                    <div></div>
                    <div className="flex space-x-4">
                      <ButtonCTASmall
                        variant="secondary"
                        type="button"
                        onClick={handleCancel}
                      >
                        <p className="px-4 font-normal text-segments-13px">
                          Cancel
                        </p>
                      </ButtonCTASmall>
                      <ButtonCTASmall
                        variant="main"
                        type="button"
                        onClick={handleApplyFilters}
                      >
                        <p className="px-4 font-bold text-segments-13px">
                          Apply
                        </p>
                      </ButtonCTASmall>
                    </div>
                  </div>
                </div>
                {/* Footer Ends */}
              </div>
            </main>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

function ManagedSegmentsView() {
  const { selected } = useAppSelector(composableFiltersState);

  const segmentDescription = selected.segment?.description;
  return (
    <div className="pr-4 mt-2 ml-10 mr-6 w-segments-800px ">
      <div className=" px-2 pt-1 border rounded-2xl  min-h-[442px] grid place-items-center">
        <div className="text-center text-gray-600 space-y-6">
          <p>{segmentDescription}</p>
          <div className="bg-[#F8F8F8] rounded-2xl py-5 px-24 space-y-1">
            <p className="text-black/50 text-base">
              Looking for your own premium segments?
            </p>
            <a
              target="_blank"
              rel="noreferrer"
              className="text-segments-link font-semibold block"
              href="https://www.tresl.co/segments-x"
            >
              {' '}
              Check out Segments X
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function InnerSegmentSelectionBar() {
  const { internal, selected } = useAppSelector(composableFiltersState);

  const isSegmentSelected = internal.selected.segmentSelectorState;
  const hasFilters = Boolean(selected.filters.conditions.length);
  const isUntitledSegment = hasFilters && !isSegmentSelected;

  return (
    <div className="flex items-center pl-12 pr-8 rounded-t-2xl h-segments-76px bg-segments-accent-green-3">
      <div className="flex items-center justify-between w-full">
        <SegmentSelector embedded />
        {hasFilters ? (
          isUntitledSegment ? (
            <SaveSegment>
              <div className="flex items-center space-x-2">
                <SaveIcon className="w-5 h-5 text-gray-800 hover:text-black" />
                <p className="font-bold">Save segment</p>
              </div>
            </SaveSegment>
          ) : (
            <div className="flex items-center">
              <FavoriteSegment />
              <EditSegment />
              <RemoveSegment />
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}

function InnerFilterBar({ advancedGrid }: { advancedGrid: boolean }) {
  const { rawQueryParams } = useParseURLQueryParams();

  const { selected } = useAppSelector(composableFiltersState);

  const history = useHistory();
  const dispatch = useAppDispatch();

  const operatorOptions = [
    {
      value: MainOperatorEnum.And,
      label: 'All',
    },
    {
      value: MainOperatorEnum.Or,
      label: 'Any',
    },
  ];

  const OperatorEnum = {
    and: 'All',
    or: 'Any',
  };

  const handleSelectOperator = (
    selectedOption: SingleValue<{
      value: MainOperatorEnum;
      label: string;
    }>
  ) => {
    dispatch(
      selectMainOperator({
        type: 'singleFilterClause',
        value: selectedOption?.value as MainOperatorEnum,
      })
    );
  };

  const selectBaseStyles = {
    input: (base: any) => ({
      ...base,
      'input:focus': {
        boxShadow: 'none',
      },
    }),
    control: (base: any) => ({
      ...base,
      backgroundColor: '#F8F8F8',
      border: 'none',
      borderRadius: '8px',
      height: '36px',
      width: '83px',
      textTransform: 'capitalize',
    }),
    menu: (base: any) => ({
      ...base,
    }),
    indicatorSeparator: () => ({}),
    valueContainer: (base: any) => ({
      ...base,
      paddingLeft: '16px',
    }),
  };

  return (
    <div className="relative mx-10 mt-6">
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2.5">
          <FilterIcon className="w-5 h-5" />

          {selected.filters.conditions.length ? (
            <p className="font-bold text-gray-500 truncate">
              Filter ({selected.filters.conditions.length})
            </p>
          ) : (
            <p className="text-black text-opacity-50 truncate">
              No filter applied
            </p>
          )}
        </div>
        {selected.filters.conditions.length ? (
          <button
            className="text-segments-link text-segments-13px"
            onClick={() => {
              dispatch(clearSelected());

              history.push({
                search: querystring.stringify(
                  omit({ ...rawQueryParams }, ['segment_name'])
                ),
              });
            }}
          >
            Reset
          </button>
        ) : null}
      </div>
      {!advancedGrid && (
        <div className="mt-5 text-black text-opacity-50">
          <div className="flex items-center space-x-3">
            <p>Result must match </p>
            <span>
              <Select
                className=""
                value={{
                  value: selected.filters.mainOperator,
                  label:
                    // @ts-ignore
                    OperatorEnum[selected.filters.mainOperator.toLowerCase()],
                }}
                isSearchable={false}
                options={operatorOptions}
                onChange={(selectedOption) => {
                  handleSelectOperator(selectedOption);
                }}
                styles={selectBaseStyles}
              />
            </span>{' '}
            <p>of the filters</p>
          </div>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// function GridViewToggle({
//   advancedGrid,
//   setAdvancedGrid,
// }: {
//   advancedGrid: boolean;
//   setAdvancedGrid: React.Dispatch<React.SetStateAction<boolean>>;
// }) {
//   return (
//     <div className="flex items-center text-gray-600 text-segments-13px">
//       <Switch.Group as="div" className="flex items-center space-x-3">
//         <Switch.Label className="tracking-wide">Simplified</Switch.Label>
//         <Switch
//           as="button"
//           checked={advancedGrid}
//           onChange={setAdvancedGrid}
//           className={`${
//             advancedGrid ? 'bg-gray-200' : 'bg-gray-200'
//           } relative inline-flex flex-shrink-0 h-4 items-center transition-colors duration-200 ease-in-out  border-transparent rounded-full cursor-pointer w-10 focus:outline-none focus:shadow-outline`}
//         >
//           {({ checked }) => (
//             <span
//               className={`${
//                 checked ? 'translate-x-5 ' : 'translate-x-0 '
//               } inline-block w-5 h-5 transition duration-200 ease-in-out transform bg-segments-accent-color-1 rounded-full`}
//             />
//           )}
//         </Switch>
//         <Switch.Label className="tracking-wide">Advanced</Switch.Label>
//       </Switch.Group>
//     </div>
//   );
// }
